import React, { useCallback, useRef, useState } from 'react';
import API_CONFIG from '../../apiConfig';
import { useAuth } from '../../context/AuthContext';

interface CustomBackgroundUploadProps {
    backgroundImageUrl: string;
    setBackgroundImageUrl: React.Dispatch<React.SetStateAction<string>>;
}

const CustomBackgroundUpload: React.FC<CustomBackgroundUploadProps> = ({
    backgroundImageUrl,
    setBackgroundImageUrl
}) => {
    const [uploading, setUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { currentUser } = useAuth();

    const hasUploadedBackground = Boolean(backgroundImageUrl);

    const handleFiles = useCallback(async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        setUploadStatus(null);
        const formData = new FormData();
        Array.from(files).forEach((file) => {
            formData.append('files', file);
        });
        formData.append('userId', currentUser?.uid || 'anonymous');

        try {
            // Use background change custom background upload endpoint
            const response = await fetch(API_CONFIG.BACKGROUND_CHANGE_UPLOAD_BACKGROUND_URL || API_CONFIG.AI_POSES_UPLOAD_URL, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                setUploadStatus('Upload failed. Please try again.');
                setBackgroundImageUrl('');
                return;
            }

            const data = await response.json();

            if (data.success) {
                if (data.uploaded && Array.isArray(data.uploaded) && data.uploaded.length > 0) {
                    setBackgroundImageUrl(data.uploaded[0].url);
                    setUploadStatus('Upload successful!');
                } else if (data.urls && Array.isArray(data.urls) && data.urls.length > 0) {
                    setBackgroundImageUrl(data.urls[0]);
                    setUploadStatus('Upload successful!');
                } else if (data.url) {
                    setBackgroundImageUrl(data.url);
                    setUploadStatus('Upload successful!');
                } else {
                    setUploadStatus('Upload failed. No file received.');
                    setBackgroundImageUrl('');
                }
            } else {
                setUploadStatus(data.message || 'Upload failed. Please try again.');
                setBackgroundImageUrl('');
            }
        } catch (error) {
            setUploadStatus('Upload failed. Please try again.');
            setBackgroundImageUrl('');
        } finally {
            setUploading(false);
        }
    }, [currentUser?.uid, setBackgroundImageUrl]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
    }, [handleFiles]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
    }, []);

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files);
    };

    return (
        <div className="h-full w-full flex flex-col overflow-hidden">
            {/* Upload area or preview - takes full height */}
            {hasUploadedBackground ? (
                <div className="bg-accent-sand rounded-lg p-1.5 border-2 border-dashed border-accent-taupe flex flex-col items-center justify-center relative h-full overflow-hidden">
                    {/* Cross button to close preview and allow new upload */}
                    <button
                        className="absolute top-0.5 right-0.5 text-text-description hover:text-red-500 text-lg focus:outline-none z-10"
                        onClick={() => {
                            setBackgroundImageUrl('');
                            setUploadStatus(null);
                        }}
                        aria-label="Remove background"
                    >
                        &times;
                    </button>
                    <h3 className="font-medium text-text-heading mb-0.5 text-[10px] flex-shrink-0">
                        Custom Background
                    </h3>
                    <div className="flex flex-col items-center justify-center w-full flex-1 overflow-hidden min-h-0">
                        <img
                            src={backgroundImageUrl}
                            alt="Custom background preview"
                            className="max-w-full max-h-full object-contain rounded shadow-md bg-white"
                        />
                    </div>
                    {uploadStatus && (
                        <p className="text-green-600 mt-0.5 text-[10px] flex-shrink-0">Upload successful!</p>
                    )}
                </div>
            ) : (
                <div
                    className="bg-accent-sand rounded-lg p-3 border-2 border-dashed border-accent-taupe flex flex-col items-center justify-center h-full"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                        accept="image/*"
                    />
                    <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center mb-2">
                        <i className="fas fa-cloud-upload-alt text-secondary-600 text-sm"></i>
                    </div>
                    <h3 className="font-medium text-text-heading mb-0.5 text-xs">Upload Background</h3>
                    <p className="text-[10px] text-text-description mb-2 text-center">Drag & drop or click</p>
                    {uploading ? (
                        <div className="flex items-center justify-center h-8">
                            <span className="inline-block w-4 h-4 border-2 border-secondary-500 border-t-transparent rounded-full animate-spin"></span>
                            <span className="ml-2 text-[10px] text-text-description">Uploading...</span>
                        </div>
                    ) : (
                        <button
                            className="bg-secondary-500 hover:bg-secondary-600 text-white px-3 py-1 rounded-lg font-medium text-xs"
                            onClick={handleButtonClick}
                            disabled={uploading}
                        >
                            Select File
                        </button>
                    )}
                    {uploadStatus && (
                        <p className={`mt-1 text-[10px] ${uploadStatus.includes('successful') ? 'text-green-600' : 'text-red-600'}`}>{uploadStatus}</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomBackgroundUpload;
